import React, { forwardRef, useEffect, useState } from 'react';
import clsx from 'clsx';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import { DropdownField } from '@/components/dropdowns/DropdownField/DropdownField';
import { ListItemDropdown } from '@/components/list-item/ListItemDropdown/ListItemDropdown';
import { DropdownMenuTrigger } from '@radix-ui/react-dropdown-menu';
import IconMoreVertical from '@/assets/icons/icon-more-vertical.svg?react';
import { SectionThumbnail } from '../SectionThumbnail';
import { Input } from '@/components/forms/Input/Input';
import type { CraftJsonDto } from '@builder4/shared/dto/craft';

interface SectionFieldSavedProps {
  className?: string;
  title?: string;
  sectionData: CraftJsonDto;
  showPublishedChip?: boolean;
  hideTitle?: boolean;
  highlightOnHover?: boolean;
  onClick?: () => void;
  onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragEnd?: (e: React.DragEvent<HTMLDivElement>) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onPublish?: () => void;
  onUnpublish?: () => void;
  onRename?: (e: string) => void;
  'data-qa-selector'?: string;
}

export const SectionFieldSaved = forwardRef<HTMLDivElement, SectionFieldSavedProps>(
  (
    {
      title = 'My Section',
      className,
      sectionData,
      showPublishedChip = false,
      hideTitle = false,
      highlightOnHover = false,
      onClick,
      onDragStart,
      onDragEnd,
      onDelete,
      onDuplicate,
      onPublish,
      onUnpublish,
      onRename,
      'data-qa-selector': dataQaSelector,
    },
    ref,
  ) => {
    const hasActions = Boolean(onDelete || onDuplicate || onPublish || onUnpublish || onRename);
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(title);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    useEffect(() => {
      if (!editing) {
        setValue(title);
      }
    }, [editing, title]);

    return (
      <div
        ref={ref}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onClick={onClick}
        data-qa-selector={dataQaSelector}
        className={clsx('inline-flex w-full max-w-[232px] flex-col items-start gap-0.5', className)}
      >
        {/* Preview card */}
        <div
          className={clsx(
            'relative w-full rounded-md border border-neutral-100 bg-contain bg-center bg-no-repeat p-3 transition-colors',
            highlightOnHover &&
              'hover:border-indigo-500 focus-visible:border-indigo-500 focus-visible:outline-none',
          )}
          tabIndex={highlightOnHover ? 0 : undefined}
          aria-label={highlightOnHover ? title : undefined}
        >
          <SectionThumbnail sectionData={sectionData} />
        </div>

        {/* Label + actions */}
        {hideTitle ? null : (
          <div className="flex h-8 w-full items-center justify-between pl-1">
            {editing && onRename ? (
              <div className="relative w-full">
                <div
                  className="absolute -top-4 left-0 w-full"
                  onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                    e.stopPropagation();
                  }}
                >
                  <Input
                    label={undefined}
                    placeholder={title}
                    containerClassName="max-w-[256px]"
                    autoFocus
                    value={value}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
                    onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                      e.stopPropagation();
                    }}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter') {
                        (e.target as HTMLInputElement).blur();
                      } else if (e.key === 'Escape') {
                        setValue(title);
                        setEditing(false);
                      }
                    }}
                    onBlur={() => {
                      const trimmedValue = value.trim();
                      setEditing(false);
                      if (!trimmedValue) {
                        setValue(title);
                        return;
                      }
                      if (trimmedValue !== title) onRename?.(trimmedValue);
                      setValue(trimmedValue);
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="flex min-w-0 flex-1 items-center">
                <span className="min-w-0 flex-1 truncate text-xs leading-none font-medium text-neutral-500">
                  {value}
                </span>
                {showPublishedChip ? (
                  <span className="mx-2 shrink-0 rounded-full bg-indigo-500 px-2 py-0.5 text-[10px] leading-tight font-semibold text-white">
                    Live
                  </span>
                ) : null}
              </div>
            )}

            {hasActions ? (
              <DropdownField
                className=""
                classNameContent="p-1 w-[140px]"
                text=""
                size="small"
                icon={null}
                open={dropdownOpen}
                onOpenChange={setDropdownOpen}
                childrenTrigger={
                  <DropdownMenuTrigger
                    asChild
                    className={clsx('cursor-pointer outline-none')}
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                    onPointerDown={(event) => {
                      event.stopPropagation();
                    }}
                  >
                    <IconSimpleButton icon={IconMoreVertical} size={28} state="default" />
                  </DropdownMenuTrigger>
                }
                childrenMenu={
                  <>
                    {[
                      onRename ? 'Rename' : null,
                      onDuplicate ? 'Duplicate' : null,
                      onPublish ? 'Publish Section' : null,
                      onUnpublish ? 'Unpublish Section' : null,
                      onDelete ? 'Delete' : null,
                    ]
                      .filter(Boolean)
                      .map((item) => (
                        <ListItemDropdown
                          key={item}
                          className="w-full cursor-pointer"
                          text={item as string}
                          shortcut=""
                          selection={false}
                          allowSelectionToggle={false}
                          handlerClick={(e) => {
                            e.stopPropagation();
                            setDropdownOpen(false);
                            if (item === 'Delete') onDelete?.();
                            if (item === 'Duplicate') onDuplicate?.();
                            if (item === 'Publish Section') onPublish?.();
                            if (item === 'Unpublish Section') onUnpublish?.();
                            if (item === 'Rename') setEditing(true);
                          }}
                          as="div"
                        />
                      ))}
                  </>
                }
              />
            ) : null}
          </div>
        )}
      </div>
    );
  },
);

SectionFieldSaved.displayName = 'SectionFieldSaved';
